import {
    RequestService,
    RequestServiceResponse,
} from '@tcc-online/request-service';
import ProjectPackage from '../types/project/ProjectPackage';
import ProjectTranslation from '../types/project/ProjectTranslation';
import ProjectJobMatchScale from '../types/jobMatch/ProjectJobMatchScale';
import ProjectJobMatchQuestionnaire from '../types/jobMatch/ProjectJobMatchQuestionnaire';
import IAdminProject from '../types/IAdminProject';
import {
    type AutoTranslateGroupsResult,
    type AutoTranslateTranslationsResult,
} from '../types/AutoTranslateResult';
import SortParams from '../types/SortParams';

const BASE_URL = '/admin/products';

const createUrl = (productId: number, projectId: number) =>
    `${BASE_URL}/${productId}/projects/${projectId}`;

const AdminProjectService = {
    createProject,
    getProject,
    saveProject,
    archiveProject,
    unarchiveProject,
    selectPackage,
    getPackages,
    getQuestionnaires,
    selectQuestionnaires,
    updateQuestionnaires,
    getTranslations,
    getTranslation,
    addLanguage,
    removeLanguage,
    saveTranslations,
    saveTranslation,
    getQuestionnaireScales,
    updateQuestionnaireScales,
    getProjectStatistics,
    deleteProject,
    copyProject,
    anonymizeProject,
    autoTranslate,
    autoTranslateGroups,
};

async function createProject(payload: {
    productId: number;
    name?: string;
    locale?: string;
}): Promise<RequestServiceResponse<{ id: number; name: string }>> {
    const { productId, name, locale } = payload;
    return await RequestService.post<
        { name?: string; locale?: string },
        { id: number; name: string }
    >(`${BASE_URL}/${productId}/projects`, { name, locale });
}

async function getProject(payload: {
    productId: number;
    projectId: number;
    locale?: string;
}): Promise<RequestServiceResponse<IAdminProject>> {
    const { productId, projectId, locale } = payload;
    return await RequestService.get<IAdminProject>(
        `${BASE_URL}/${productId}/projects/${projectId}`,
        {},
        locale ? { headers: { Language: locale } } : {},
    );
}

export interface SaveProjectPayload {
    productId: number;
    projectId: number;
    name?: string;
    note?: string;
    consentText?: string;
    enableProjectName?: boolean;
    enableResults?: boolean;
    enableAdditionalDescription?: boolean;
    locale?: string;
    unit?: string;
    level?: string;
    position?: string;
    primaryLanguage?: string;
    phase?: number;
}

async function saveProject(
    payload: SaveProjectPayload,
): Promise<RequestServiceResponse<IAdminProject>> {
    const {
        productId,
        projectId,
        level,
        unit,
        position,
        primaryLanguage,
        ...rest
    } = payload;

    const body: Record<string, unknown> = { primaryLanguage, ...rest };
    if (level || unit || position) {
        body.config = {};
        if (level) {
            (body.config as Record<string, unknown>).level = level;
        }
        if (unit) {
            (body.config as Record<string, unknown>).unit = unit;
        }
        if (position) {
            (body.config as Record<string, unknown>).position = position;
        }
    }

    return await RequestService.patch<Record<string, unknown>, IAdminProject>(
        `${BASE_URL}/${productId}/projects/${projectId}`,
        body,
        { allowMultiRequest: true },
    );
}

async function archiveProject(payload: {
    productId: number;
    projectId: number;
}): Promise<RequestServiceResponse<IAdminProject>> {
    const { productId, projectId } = payload;
    return await RequestService.post<Record<string, never>, IAdminProject>(
        `${BASE_URL}/${productId}/projects/${projectId}/archive`,
        {},
    );
}

async function unarchiveProject(payload: {
    productId: number;
    projectId: number;
}): Promise<RequestServiceResponse<IAdminProject>> {
    const { productId, projectId } = payload;
    return await RequestService.post<Record<string, never>, IAdminProject>(
        `${BASE_URL}/${productId}/projects/${projectId}/unarchive`,
        {},
    );
}

async function selectPackage(payload: {
    id: number;
}): Promise<RequestServiceResponse> {
    return await RequestService.post<unknown>(BASE_URL, { id: payload.id });
}

async function getPackages(payload: {
    productId: number;
    projectId: number;
}): Promise<RequestServiceResponse<ProjectPackage[]>> {
    const { productId, projectId } = payload;
    return await RequestService.get<ProjectPackage[]>(
        `${BASE_URL}/${productId}/projects/${projectId}/packages`,
    );
}

async function getQuestionnaires(
    payload: {
        productId: number;
        projectId: number;
    } & SortParams,
): Promise<RequestServiceResponse<ProjectJobMatchQuestionnaire[]>> {
    const { productId, projectId, sortBy, sortOrder } = payload;
    return await RequestService.get<ProjectJobMatchQuestionnaire[]>(
        `${BASE_URL}/${productId}/projects/${projectId}/questionnaires`,
        { sortBy, sortOrder },
    );
}

async function selectQuestionnaires(payload: {
    productId: number;
    projectId: number;
    selectedQuestionnaires: { id: number }[];
}): Promise<RequestServiceResponse<IAdminProject>> {
    const { productId, projectId, selectedQuestionnaires } = payload;
    return await RequestService.patch<
        { selectedQuestionnaires: { id: number }[] },
        IAdminProject
    >(`${BASE_URL}/${productId}/projects/${projectId}`, {
        selectedQuestionnaires,
    });
}

async function updateQuestionnaires(payload: {
    productId: number;
    projectId: number;
    questionnaires: { importance: number; id: number }[];
}): Promise<RequestServiceResponse<ProjectJobMatchQuestionnaire[]>> {
    const { productId, projectId, questionnaires } = payload;
    return await RequestService.patch<
        { importance: number; id: number }[],
        ProjectJobMatchQuestionnaire[]
    >(
        `${BASE_URL}/${productId}/projects/${projectId}/questionnaires`,
        questionnaires,
    );
}

async function getTranslations(payload: {
    productId: number;
    projectId: number;
    locale: string;
}): Promise<RequestServiceResponse<ProjectTranslation[]>> {
    const { productId, projectId, locale } = payload;
    return await RequestService.get<ProjectTranslation[]>(
        `${BASE_URL}/${productId}/projects/${projectId}/translations?locale=${locale}`,
    );
}

async function getTranslation(payload: {
    productId: number;
    projectId: number;
    locale: string;
}): Promise<RequestServiceResponse<ProjectTranslation>> {
    const { productId, projectId, locale } = payload;
    return await RequestService.get<ProjectTranslation>(
        `${BASE_URL}/${productId}/projects/${projectId}/translations?locale=${locale}`,
    );
}

async function addLanguage(payload: {
    productId: number;
    projectId: number;
    locale: string;
    locales: string[];
}): Promise<RequestServiceResponse<unknown>> {
    const { productId, projectId, locale, locales } = payload;
    return await RequestService.patch<unknown>(
        `${BASE_URL}/${productId}/projects/${projectId}`,
        { locales: [...locales, locale] },
    );
}

async function removeLanguage(payload: {
    productId: number;
    projectId: number;
    locale: string;
    locales: string[];
}): Promise<RequestServiceResponse<unknown>> {
    const { productId, projectId, locale, locales } = payload;
    return await RequestService.patch<unknown>(
        `${BASE_URL}/${productId}/projects/${projectId}`,
        { locales: locales.filter((l) => l !== locale) },
    );
}

async function saveTranslations(payload: {
    productId: number;
    projectId: number;
    locale: string;
    translations: ProjectTranslation[];
}): Promise<RequestServiceResponse<unknown>> {
    const { productId, projectId, locale, translations } = payload;
    return await RequestService.patch<unknown>(
        `${BASE_URL}/${productId}/projects/${projectId}/translations?locale=${locale}`,
        translations,
    );
}

async function saveTranslation(payload: {
    productId: number;
    projectId: number;
    locale: string;
    id: number;
    value: string;
}): Promise<RequestServiceResponse<unknown>> {
    const { productId, projectId, id, locale, value } = payload;
    return await RequestService.patch<unknown>(
        `${BASE_URL}/${productId}/projects/${projectId}/translations/${id}?locale=${locale}`,
        { value },
    );
}

async function getQuestionnaireScales(payload: {
    productId: number;
    projectId: number;
    questionnaireId: number;
}): Promise<RequestServiceResponse<ProjectJobMatchScale[]>> {
    const { productId, projectId, questionnaireId } = payload;
    return await RequestService.get<ProjectJobMatchScale[]>(
        `${BASE_URL}/${productId}/projects/${projectId}/questionnaires/${questionnaireId}/scales`,
    );
}

async function updateQuestionnaireScales(payload: {
    productId: number;
    projectId: number;
    questionnaireId: number;
    scaleId: number;
    importance: number;
    selectedRange: [number, number];
}): Promise<RequestServiceResponse<ProjectJobMatchScale>> {
    const {
        productId,
        projectId,
        questionnaireId,
        scaleId,
        importance,
        selectedRange,
    } = payload;
    const body: Record<string, unknown> = { importance };
    if (selectedRange && selectedRange.length > 0) {
        body.selectedRange = selectedRange;
    }
    return await RequestService.patch<
        { importance: number; selectedRange?: number[] },
        ProjectJobMatchScale
    >(
        `${BASE_URL}/${productId}/projects/${projectId}/questionnaires/${questionnaireId}/scales/${scaleId}`,
        body as { importance: number; selectedRange?: number[] },
    );
}

async function getProjectStatistics(payload: {
    productId: number;
    projectId: number;
}): Promise<RequestServiceResponse<unknown>> {
    const { productId, projectId } = payload;
    return await RequestService.get<unknown>(
        `${BASE_URL}/${productId}/projects/${projectId}/statistics`,
    );
}

async function deleteProject(payload: {
    productId: number;
    projectId: number;
}): Promise<RequestServiceResponse<unknown>> {
    const { productId, projectId } = payload;
    return await RequestService.delete(createUrl(productId, projectId));
}

async function copyProject(payload: {
    productId: number;
    projectId: number;
    name: string;
}) {
    const { productId, projectId, name } = payload;
    return await RequestService.post<{ name: string }, IAdminProject>(
        `${BASE_URL}/${productId}/projects/${projectId}/copy`,
        { name },
    );
}

async function anonymizeProject(payload: {
    productId: number;
    projectId: number;
}) {
    const { productId, projectId } = payload;
    return await RequestService.post<unknown>(
        `${BASE_URL}/${productId}/projects/${projectId}/anonymize`,
    );
}

async function autoTranslate(payload: {
    productId: number;
    projectId: number;
    locale: string;
}): Promise<RequestServiceResponse<AutoTranslateTranslationsResult>> {
    const { productId, projectId, locale } = payload;
    return await RequestService.post<
        undefined,
        AutoTranslateTranslationsResult
    >(
        `${BASE_URL}/${productId}/projects/${projectId}/translations/auto-translate?locale=${locale}`,
    );
}

async function autoTranslateGroups(payload: {
    productId: number;
    projectId: number;
    locale: string;
}): Promise<RequestServiceResponse<AutoTranslateGroupsResult>> {
    const { productId, projectId, locale } = payload;
    return await RequestService.post<undefined, AutoTranslateGroupsResult>(
        `${BASE_URL}/${productId}/projects/${projectId}/group-translations/auto-translate?locale=${locale}`,
    );
}

export default AdminProjectService;
